import {useCallback} from 'react'
import invariant from 'tiny-invariant'

import {omit} from '../../../../utils/omit'
import {apiImportIssueFields} from '../../../api/columns/api-import-issue-fields'
import {cancelGetAllMemexData} from '../../../api/memex/api-get-all-memex-data'
import {DefaultOmitPropertiesForView} from '../../../api/view/contracts'
import {useApiRequest} from '../../../hooks/use-api-request'
import {useUpdateViewWithoutSettingStates} from '../../../hooks/use-view-apis'
import {ViewStateActionTypes} from '../../../hooks/use-view-state-reducer/view-state-action-types'
import {useViews} from '../../../hooks/use-views'
import type {ColumnModel} from '../../../models/column-model'
import {type AddColumnRequest, buildColumnModels} from '../../../state-providers/columns/columns-state-provider'
import {useAddColumn} from '../../../state-providers/columns/use-add-column'
import {useAllColumns} from '../../../state-providers/columns/use-all-columns'

export const useAddField = () => {
  const {viewStateDispatch, viewsMap} = useViews()
  const updateViewWithoutSettingStates = useUpdateViewWithoutSettingStates()
  const {addColumn} = useAddColumn()
  const {setAllColumns, allColumnsRef} = useAllColumns()

  const updateViewWithColumns = useCallback(
    async (columns: Array<ColumnModel>, viewNumber?: number) => {
      if (!viewNumber) {
        return
      }

      const view = viewsMap[viewNumber]
      invariant(view, 'View must exist')

      const newColumnDatabaseIds = columns.map(col => col.databaseId)
      const nextServerViewState = {
        ...view.serverViewState,
        visibleFields: view.serverViewState.visibleFields.concat(newColumnDatabaseIds),
      }

      await updateViewWithoutSettingStates.perform({
        viewNumber: view.number,
        view: omit(nextServerViewState, DefaultOmitPropertiesForView),
      })

      if (updateViewWithoutSettingStates.status.current.status === 'succeeded') {
        for (const column of columns) {
          viewStateDispatch({
            type: ViewStateActionTypes.AddField,
            column,
            viewNumber,
          })
        }
      }
    },
    [updateViewWithoutSettingStates, viewStateDispatch, viewsMap],
  )

  const requestAddField = useCallback(
    async ({request, viewNumber}: {request: AddColumnRequest; viewNumber?: number}) => {
      const {newColumn} = await addColumn(request)

      await updateViewWithColumns([newColumn], viewNumber)

      return newColumn
    },
    [addColumn, updateViewWithColumns],
  )

  const requestImportIssueFields = useCallback(
    async ({issueFieldIds, viewNumber}: {issueFieldIds: Array<number>; viewNumber?: number}) => {
      cancelGetAllMemexData()
      const {memexProjectColumns} = await apiImportIssueFields({issueFieldIds})

      const newColumnModels = buildColumnModels(memexProjectColumns)

      const newColumnIds = new Set(newColumnModels.map(col => col.id))
      // Ensure new column hasn't already been added to new column state
      const filteredColumns = allColumnsRef.current.filter(col => !newColumnIds.has(col.id))
      const newColumns = [...filteredColumns, ...newColumnModels]

      setAllColumns(newColumns)

      await updateViewWithColumns(newColumnModels, viewNumber)

      return newColumnModels
    },
    [setAllColumns, updateViewWithColumns, allColumnsRef],
  )

  const addField = useApiRequest({request: requestAddField})
  const importIssueFields = useApiRequest({request: requestImportIssueFields, showErrorToast: false})

  return {addField, importIssueFields}
}
